import { Control } from '@Common/Controls/Controls';
import { DropDown } from '@Common/Controls/DropDown';
import { GradientPreviewControl } from '@Common/Controls/GradientEditor/GradientPreviewControl';
import { GradientSliderControl } from '@Common/Controls/GradientEditor/GradientSliderControl';
import { GridLayout } from '@Common/Controls/GridLayout';
import { MulticastDelegateBag } from '@Common/Core/MulticastDelegate';
import { clone } from '@Common/Helpers/CommonUtils';
import { GradientBackground, LinearGradientBackground, RadialGradientBackground } from '@InstanceFactory';

export interface IGradientEditorSetting {}

enum GradientDirection {
    horizontal,
    vertical,
    diagonal45,
    diagonal135,
    none
}

enum GradientType {
    linear,
    radial
}

const directionItems = [
    { id: GradientDirection.horizontal, value: strings.gradientDirections.horizontal },
    { id: GradientDirection.vertical, value: strings.gradientDirections.vertical },
    { id: GradientDirection.diagonal45, value: strings.gradientDirections.diagonal45 },
    { id: GradientDirection.diagonal135, value: strings.gradientDirections.diagonal135 }
];

function setGradientDirection(lg: LinearGradientBackground, direction: GradientDirection): void {
    switch (direction) {
        case GradientDirection.horizontal:
            lg.startX = 0;
            lg.endX = 1;
            lg.startY = lg.endY = 0;
            return;
        case GradientDirection.vertical:
            lg.startY = 0;
            lg.endY = 1;
            lg.startX = lg.endX = 0;
            return;
        case GradientDirection.diagonal45:
            lg.startX = 0;
            lg.endX = 1;
            lg.startY = 0;
            lg.endY = -1;
            return;
        case GradientDirection.diagonal135:
            lg.startX = 0;
            lg.endX = 1;
            lg.startY = 0;
            lg.endY = 1;
            return;
    }
}

function getGradientDirection(lg: LinearGradientBackground): GradientDirection {
    const dirX = lg.endX - lg.startX;
    const dirY = lg.endY - lg.startY;
    if (dirX !== 0) {
        if (dirY > 0) {
            return GradientDirection.diagonal135;
        }
        if (dirY < 0) {
            return GradientDirection.diagonal45;
        }
        return GradientDirection.horizontal;
    }
    if (dirY !== 0) {
        return GradientDirection.vertical;
    }
    return GradientDirection.none;
}

function getGradientType(gr: GradientBackground): GradientType {
    const typeName = gr.$type;
    if (LinearGradientBackground.typeName === typeName) {
        return GradientType.linear;
    }
    if (RadialGradientBackground.typeName === typeName) {
        return GradientType.radial;
    }
    const isLinear = linearGradientProperties.every((prop: string) => gr.hasOwnProperty(prop));
    if (isLinear) {
        return GradientType.linear;
    }
    return GradientType.radial;
}

const linearGradientProperties: string[] = ['startX', 'endX', 'startY', 'endY'];

export class GradientEditor implements Control {
    private currentValue: GradientBackground;
    private root: GridLayout;
    private readonly left: GridLayout;
    private readonly sliderCtrl: GradientSliderControl;
    private readonly previewCtrl: GradientPreviewControl;
    private readonly direction: DropDown;
    private readonly events = new MulticastDelegateBag();

    public readonly onChanged = this.events.create();
    public readonly onOk = this.events.create();
    public readonly onCancel = this.events.create();

    constructor(setting: IGradientEditorSetting) {
        this.direction = new DropDown({ sortable: false });
        this.sliderCtrl = new GradientSliderControl();
        this.previewCtrl = new GradientPreviewControl();

        this.left = new GridLayout({
            rows: [{ style: { height: '*' } }, { style: { height: 'auto' } }],
            columns: [{}],
            cells: [
                { rowIndex: 0, columnIndex: 0, content: this.previewCtrl },
                { rowIndex: 1, columnIndex: 0, content: this.direction, style: { paddingTop: '2px' } }
            ]
        });
        // var btnOk = new Button({text:'Ok'});
        // var btnCancel = new Button({ text: 'Cancel' });

        const right: GridLayout = new GridLayout({
            rows: [{ style: { height: '*' } }, { style: { height: 'auto' } }],
            columns: [{ style: { width: '*' } }, { style: { width: 'auto' } }, { style: { width: 'auto' } }],
            cells: [{ rowIndex: 0, columnIndex: 0, colSpan: 3, content: this.sliderCtrl, style: { padding: '10' } }]
        });
        this.sliderCtrl.changedEvent.add(() => {
            this.previewCtrl.update(this.currentValue);
        });

        this.root = new GridLayout({
            rows: [{}],
            columns: [{ style: { width: '100px' } }, {}],
            cells: [
                { rowIndex: 0, columnIndex: 0, content: this.left },
                { rowIndex: 0, columnIndex: 1, content: right }
            ]
        });

        this.direction.setItems(clone(directionItems));
        this.direction.onChange((val: any[]) => {
            const background = this.currentValue as LinearGradientBackground;
            setGradientDirection(background, val[0].id);
            this.previewCtrl.update(this.currentValue);
        });
    }

    public value(value: GradientBackground): GradientBackground {
        if (arguments.length === 0) {
            return this.currentValue;
        }

        this.currentValue = value;

        this.update();
        return this.currentValue;
    }

    public getControl(): JQuery {
        return this.root.getControl();
    }

    public adjust(w: number, h: any): void {
        const lh = parseInt(h) || this.root.getControl().outerHeight();
        this.root.setColumnStyle(0, { width: lh + 'px' });
        return this.root.adjust(w, h);
    }

    public update(): void {
        const type = getGradientType(this.currentValue);
        if (type === GradientType.radial) {
            this.direction.getControl().hide();
            this.left.setRowStyle(1, { height: '0px' });
        } else if (type === GradientType.linear) {
            this.direction.getControl().show();
            this.left.setRowStyle(1, { height: 'auto' });

            const lg = this.currentValue as LinearGradientBackground;
            let direction = getGradientDirection(lg);
            if (direction === GradientDirection.none) {
                direction = GradientDirection.horizontal;
                setGradientDirection(lg, direction);
            }
            this.direction.selectedItems([this.direction.findItemById(direction)]);
        }

        this.sliderCtrl.value(this.currentValue);
        this.previewCtrl.update(this.currentValue);
    }

    public setState(enabled: boolean): void {
        this.root.setState(enabled);
    }
}
